import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AiDocumentsService } from './ai-documents.service';
import { FileInputComponent } from '@shared/components/file-input/file-input.component';
import { ButtonModule } from '@primeng/button';
import { ModalService } from '@shared/services/modal/modal.service';
import { AiDocumentResultComponent } from './ai-document-result.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

declare const pdfjsLib: any;
declare const Tesseract: any;

@Component({
  selector: 'app-ai-document-upload',
  standalone: true,
  imports: [ReactiveFormsModule, FileInputComponent, ButtonModule, SpinnerComponent, TranslatePipe],
  template: `
    @if (isSubmitting) {
      <div style="font-size: 32px; display: flex; justify-content: center; align-items: center; height: 300px">
        <spinner/>
      </div>
    } @else {
      <form [formGroup]="form" (ngSubmit)="onSubmit()" class="p-3 w-100">
        <kep-file-input formControlName="file" [limit]="10" label="AI_DOCUMENTS.FILE"></kep-file-input>
        @if (isDetecting) {
          <div class="mt-2" style="display: flex; justify-content: center">
            <spinner/>
          </div>
        } @else if (documentType) {
          <div class="mt-2">{{ 'AI_DOCUMENTS.DOCUMENTY_TYPE' | translate }}: {{ documentType }}</div>
        }
        <div class="mt-2">
          <textarea
            class="form-control"
            rows="3"
            formControlName="prompt"
            [placeholder]="'AI_DOCUMENTS.PROMPT' | translate"
          ></textarea>
        </div>
        <div class="w-100 mt-2">
          <button [disabled]="form.invalid || isDetecting" class="w-100 btn btn-primary full-width">
            {{ 'AI_DOCUMENTS.PROCEED' | translate }}
          </button>
        </div>
      </form>
    }
  `,
})
export class AiDocumentUploadComponent {
  isSubmitting = false;
  isDetecting = false;
  documentType: 'AGREEMENT' | 'INVOICE' | null = null;
  private fb = inject(FormBuilder);
  form: FormGroup = this.fb.group({
    file: [null, Validators.required],
    prompt: [''],
  });
  private service = inject(AiDocumentsService);
  private modal = inject(ModalService);
  private translate = inject(TranslateService);

  ngOnInit() {
    this.form.get('file')?.valueChanges.subscribe((file: File) => {
      if (file) {
        this.detectDocumentType(file);
      } else {
        this.documentType = null;
      }
    });
  }

  async detectDocumentType(file: File) {
    this.isDetecting = true;
    this.documentType = null;
    try {
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({data: arrayBuffer}).promise;

      const invoiceSignals = [
        {re: /\b(invoice|proforma invoice|rechnung)\b/i, w: 4},
        {re: /\b(invoice\s*(no|nr|#|number)|rechnungsnummer)\b/i, w: 3},
        {re: /\b(total|subtotal|balance due|due date|vat|mwst|u[st]-?id|ust-?idnr)\b/i, w: 2},
        {re: /\b(iban|bic|swift|bankverbindung|bank details)\b/i, w: 2},
        {re: /\b(qty|quantity|unit price|amount|menge|stückpreis|betrag|gesamt)\b/i, w: 1},
        {re: /[\$\€\£\¥]|\b(usd|eur|gbp|chf|jpy|cny)\b/i, w: 1},
      ];

      const agreementSignals = [
        {re: /\b(miet[-\s]*vertrag|vertrag|agreement|contract)\b/i, w: 4},
        {re: /\b(whereas|hereinafter|governing law|term|termination|liability|confidentiality|force majeure)\b/i, w: 2},
        {re: /\b(mietzeit|vermieter|mieter|haftung|vertraulichkeit|höhere gewalt|geltendes recht)\b/i, w: 2},
        {re: /§\s*\d+|\b(section|article)\s+\d+/i, w: 1},
        {re: /\b(signature|signatures|unterschrift)\b/i, w: 1},
      ];


      const score = (text: string, rules: { re: RegExp; w: number }[]) =>
        rules.reduce((s, r) => s + (r.re.test(text) ? r.w : 0), 0);

      const page = await pdf.getPage(1);
      let pageText = '';
      try {
        const tc = await page.getTextContent();
        pageText = tc.items.map((i: any) => i.str).join(' ');
      } catch {
      }

      if (!pageText || pageText.trim().length < 20) {
        const viewport = page.getViewport({scale: 1.5});
        const offCanvas = document.createElement('canvas');
        offCanvas.width = viewport.width;
        offCanvas.height = viewport.height;
        await page.render({canvasContext: offCanvas.getContext('2d'), viewport}).promise;
        try {
          const rec = await Tesseract.recognize(offCanvas.toDataURL('image/png'), 'eng+deu');
          pageText = rec.data.text || '';
        } catch {
        }
      }

      const lower = pageText.toLowerCase();
      const inv = score(lower, invoiceSignals);
      const agr = score(lower, agreementSignals);
      const hasInvKeyword = /\b(invoice|rechnung)\b/i.test(lower);

      this.documentType = hasInvKeyword && inv >= agr ? 'INVOICE' : 'AGREEMENT';
    } catch {
      this.documentType = null;
    } finally {
      this.isDetecting = false;
    }
  }

  onSubmit() {
    if (this.form.invalid) return;
    const data = new FormData();
    data.append('file', this.form.value.file);
    data.append('prompt', this.form.value.prompt);
    if (this.documentType) {
      data.append('document_type', this.documentType);
    }
    this.isSubmitting = true;
    this.service.upload(data).subscribe(res => {
      this.modal.close();
      this.modal.open({
        component: AiDocumentResultComponent,
        title: this.translate.instant('AI_DOCUMENTS.TITLE'),
        modalOptions: {size: 'xl', fullscreen: true, windowClass: 'ai-document-result'},
        componentOptions: {response: res}
      });
    }).add(() => {
      this.isSubmitting = false;
    });
  }
}

